import React, { useCallback, useEffect, useRef, useState } from 'react';

import '../teacher_payments.css';
import './base_rate.css';
import { fmt, is_permission_error } from '../format';
import { fetch_base_rate_history } from '../api';
import BaseRateHistoryDialog from './history_dialog';

/*
 * the teacher profile's "Base rate" line and its Rate history pop-up. The rate comes from the
 * history API (current_rate), else the profile's base_rate; the button shows only when can_change.
 */
const BaseRateLine = ({
  teacher_uuid,
  teacher_name,
  profile_base_rate,
  on_notify,
  on_changed = undefined,
  now = undefined
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  // Only the latest request may land: a teacher switch or a quick reload must not show stale history.
  const seq = useRef(0);

  const load = useCallback(() => {
    if (!teacher_uuid) return;
    seq.current += 1;
    const mine = seq.current;
    setLoading(true);
    setError('');
    fetch_base_rate_history(teacher_uuid)
      .then((d) => {
        if (mine !== seq.current) return;
        if (!d || !Array.isArray(d.entries)) throw new Error('unexpected base rate history shape');
        setData(d);
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        // No grant: keep the profile's rate, no button, no error.
        if (is_permission_error(e)) {
          setData(null);
          return;
        }
        console.error('base rate history load failed', e?.response?.status || e?.message);
        setError('Could not load the rate history.');
      })
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
  }, [teacher_uuid]);

  useEffect(() => {
    setData(null);
    setOpen(false);
    load();
  }, [load]);

  if (!teacher_uuid) return null;

  const rate = data && data.current_rate ? data.current_rate : profile_base_rate;
  const can_change = !!(data && data.can_change);

  const open_dialog = () => {
    setOpen(true);
    load();
  };
  const on_saved = (text) => {
    if (on_notify) on_notify('success', text);
    load();
    if (on_changed) on_changed();
  };

  return (
    <div className="tp-card tp-base-rate-line">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" role="heading" aria-level={2}>
          Base rate
        </span>
        {can_change ? (
          <span className="tp-card-hd-acts">
            <button
              type="button"
              className="tp-btn-out"
              onClick={open_dialog}
              aria-haspopup="dialog"
            >
              Rate history
            </button>
          </span>
        ) : null}
      </div>
      <div className="tp-br-body">
        <span className="tp-br-value" data-testid="base-rate-value">
          {rate || rate === 0 ? `${fmt(rate)} per 60 min` : '—'}
        </span>
        {loading && !data ? <span className="tp-sub">Loading rate history…</span> : null}
        {error && !open ? (
          <span className="tp-err">
            {error}{' '}
            <button type="button" className="tp-link" onClick={load}>
              Try again
            </button>
          </span>
        ) : null}
      </div>
      {open ? (
        <BaseRateHistoryDialog
          teacher_uuid={teacher_uuid}
          teacher_name={teacher_name}
          data={data}
          loading={loading}
          error={error}
          on_retry={load}
          on_saved={on_saved}
          on_close={() => setOpen(false)}
          now={now}
        />
      ) : null}
    </div>
  );
};

export default BaseRateLine;
